import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Card, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, MenuItem, Chip, IconButton, Alert,
  Autocomplete, Divider, List, ListItem, ListItemText, Tooltip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, CircularProgress
} from '@mui/material';
import { Add, CheckCircle, AttachFile, Close } from '@mui/icons-material';
import { blockagesAPI, projectsAPI, activitiesAPI } from '../../services/api';
import FileUpload from '../../components/common/FileUpload';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import dayjs from 'dayjs';
import 'dayjs/locale/es';

dayjs.locale('es');

const emptyForm = {
  projectId: '', description: '', relatedPeople: [],
  requirements: '', evidences: []
};

export default function Blockages() {
  const [blockages, setBlockages] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [peopleOptions, setPeopleOptions] = useState([]);
  const [peopleInput, setPeopleInput] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [resolveConfirm, setResolveConfirm] = useState({ open: false, id: null });

  const load = async () => {
    try {
      const [blkRes, prjRes] = await Promise.all([
        blockagesAPI.getAll({ status: statusFilter !== 'all' ? statusFilter : undefined }),
        projectsAPI.getAll()
      ]);
      setBlockages(blkRes.data?.blockages || blkRes.data || []);
      setProjects(prjRes.data);
    } catch { } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [statusFilter]);

  const handlePeopleInput = async (val) => {
    setPeopleInput(val);
    if (val.length < 2) return;
    try {
      const res = await activitiesAPI.peopleAutocomplete(val);
      setPeopleOptions(res.data?.suggestions || []);
    } catch { }
  };

  const handleFilesUploaded = (files) => {
    setForm(f => ({ ...f, evidences: [...f.evidences, ...files.map(fl => fl.key)] }));
  };

  const handleSave = async () => {
    setError(''); setSaving(true);
    try {
      await blockagesAPI.create(form);
      setOpenDialog(false);
      setForm(emptyForm);
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al reportar bloqueo');
    } finally { setSaving(false); }
  };

  const handleResolve = async (id) => {
    try {
      await blockagesAPI.resolve(id);
      setResolveConfirm({ open: false, id: null });
      load();
    } catch (err) { alert(err.response?.data?.error || 'Error'); }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h5" fontWeight={700}>Bloqueos</Typography>
          <Typography variant="body2" color="text.secondary">Reporta y gestiona impedimentos en tu trabajo</Typography>
        </Box>
        <Button variant="contained" color="error" startIcon={<Add />}
          onClick={() => { setForm(emptyForm); setError(''); setOpenDialog(true); }}>
          Reportar Bloqueo
        </Button>
      </Box>

      {/* Filter */}
      <Box display="flex" gap={1} mb={2}>
        {['active', 'resolved', 'all'].map(s => (
          <Chip key={s}
            label={s === 'active' ? 'Activos' : s === 'resolved' ? 'Resueltos' : 'Todos'}
            variant={statusFilter === s ? 'filled' : 'outlined'}
            color={s === 'active' ? 'error' : s === 'resolved' ? 'success' : 'default'}
            onClick={() => setStatusFilter(s)}
            clickable
          />
        ))}
      </Box>

      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Proyecto</TableCell>
                <TableCell>Descripción</TableCell>
                <TableCell>Personas</TableCell>
                <TableCell>Fecha</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {blockages.map(blk => {
                const proj = projects.find(p => p._id === blk.projectId);
                return (
                  <TableRow key={blk._id} hover>
                    <TableCell>{proj?.name || blk.projectName || '—'}</TableCell>
                    <TableCell sx={{ maxWidth: 280 }}>
                      <Tooltip title={blk.description}>
                        <Typography variant="body2" noWrap>{blk.description}</Typography>
                      </Tooltip>
                      {blk.requirements && (
                        <Typography variant="caption" color="text.secondary" display="block" noWrap>
                          Req: {blk.requirements}
                        </Typography>
                      )}
                      {blk.evidences?.length > 0 && (
                        <Box display="flex" alignItems="center" gap={0.5} mt={0.5}>
                          <AttachFile sx={{ fontSize: 12, color: 'text.secondary' }} />
                          <Typography variant="caption" color="text.secondary">{blk.evidences.length} evidencia(s)</Typography>
                        </Box>
                      )}
                    </TableCell>
                    <TableCell>
                      {blk.relatedPeople?.length > 0
                        ? <Box display="flex" flexWrap="wrap" gap={0.5}>
                            {blk.relatedPeople.map((p, i) => <Chip key={i} label={p} size="small" />)}
                          </Box>
                        : <Typography variant="caption" color="text.secondary">—</Typography>}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{dayjs(blk.createdAt).format('DD/MM/YYYY')}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={blk.status === 'active' ? 'Activo' : 'Resuelto'}
                        size="small"
                        color={blk.status === 'active' ? 'error' : 'success'}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: '2px' }}>
                        {blk.status === 'active' && (
                          <Tooltip title="Marcar como resuelto">
                            <IconButton size="small" sx={{ borderRadius: '5px', bgcolor: 'rgba(0,0,0,0.06)', '&:hover': { bgcolor: 'rgba(0,0,0,0.13)' } }}
                              onClick={() => setResolveConfirm({ open: true, id: blk._id })}>
                              <CheckCircle fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
              {blockages.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body2" color="text.secondary" py={3}>
                      {statusFilter === 'active' ? 'No hay bloqueos activos. ¡Todo en orden!' : 'Sin bloqueos.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Report blockage dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: 'error.main' }}>Reportar Bloqueo</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField select label="Proyecto *" value={form.projectId}
              onChange={e => setForm(f => ({ ...f, projectId: e.target.value }))} fullWidth>
              {projects.map(p => <MenuItem key={p._id} value={p._id}>{p.name}</MenuItem>)}
            </TextField>
            <TextField label="Descripción del bloqueo *" value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              multiline rows={3} fullWidth placeholder="Describe el impedimento que encontraste..." />
            <TextField label="Requerimientos / Lo que necesitas" value={form.requirements}
              onChange={e => setForm(f => ({ ...f, requirements: e.target.value }))}
              multiline rows={2} fullWidth placeholder="¿Qué necesitas para desbloquear?" />
            <Autocomplete
              multiple freeSolo
              options={peopleOptions}
              value={form.relatedPeople}
              inputValue={peopleInput}
              onInputChange={(_, val) => handlePeopleInput(val)}
              onChange={(_, val) => setForm(f => ({ ...f, relatedPeople: val }))}
              renderInput={(params) => (
                <TextField {...params} label="Personas involucradas" placeholder="Email o nombre" fullWidth />
              )}
              noOptionsText="Sin sugerencias"
            />
            <Box>
              <Divider sx={{ mb: 1.5 }} />
              <Typography variant="subtitle2" mb={1}>Evidencias (opcional)</Typography>
              {form.evidences.length > 0 && (
                <Box display="flex" flexWrap="wrap" gap={1} mb={1}>
                  {form.evidences.map((ev, idx) => (
                    <Chip key={idx} label={ev.split('/').pop()?.substring(0, 30) || `Archivo ${idx + 1}`}
                      onDelete={() => setForm(f => ({ ...f, evidences: f.evidences.filter((_, i) => i !== idx) }))}
                      deleteIcon={<Close />} icon={<AttachFile />} size="small" variant="outlined" />
                  ))}
                </Box>
              )}
              <FileUpload onFilesUploaded={handleFilesUploaded} maxFiles={3} />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={handleSave}
            disabled={saving || !form.projectId || !form.description}>
            {saving ? 'Guardando...' : 'Reportar Bloqueo'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={resolveConfirm.open} title="Marcar como Resuelto"
        message="¿Confirmas que este bloqueo ha sido resuelto?"
        confirmLabel="Resolver"
        onConfirm={() => handleResolve(resolveConfirm.id)}
        onCancel={() => setResolveConfirm({ open: false, id: null })}
      />
    </Box>
  );
}
